import cx from "classnames";
import React, { Fragment, useState, useRef, useMemo, useEffect } from "react";
import { ChangeAccordionGroup } from "@/appComponents/Navigation/RightSidebar/ChangesDisplay/ChangesDisplay";

import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { BigInfoIcon, Flex, OverflowText, Tabs, isNullish } from "@theorgchartdev/storybook";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import {
  BuildPlanActivityOverrideActions,
  DoesGroupHaveConflict,
  GetChangeAccordionLabel,
  GetFilteredAndSortedOverrides,
  GetGroupIconByType,
  GetLabelFromMismatch,
  GetPlanActivityMismatchActions,
  GetUnfilteredActiveOverrides,
  GroupChangesForHistory,
  GroupOverridesByChangeType,
  GroupOverridesByObjType,
  GroupOverridesForHistory,
  NormalizeAccordionGroup
} from "./Activity.utils";
import { ActivityHeader } from "./ActivityHeader";
import { GetDoc, GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { MismatchDisplayRow } from "../MismatchDisplayRow";
import { appstr } from "@coreapp/utils/appstr";
import { appstrp } from "@coreapp/utils/appstrp";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { isBlockedField } from "@coreapp/utils/FormatFieldValue";
import {
  AssignmentStatus,
  enumObjectTypes,
  kEmployeeAssignmentStatusID,
  kEmployeePositionID,
  kNameID,
  enumOverrideType
} from "@coreapp/constants";
import { GetFlattenedChanges } from "@coreapp/utils/GetFlattenedChanges";
import { ChangesItemDisplay } from "./ChangesItemDisplay";
import { promptToReopenPosition } from "@/appComponents/Blocks/Menus/PositionContextMenu/openPosition";
import { ApprovalsTab } from "./ApprovalsTab/ApprovalsTab";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import ActivityFooter from "./ActivityFooter";

/**
 * @typedef {import('./Activity.utils').ChangeDisplayItem} ChangeDisplayItem
 */

const enumDisplayMode = {
  summary: { label: "Summary", value: 0, shouldShow: () => true },
  history: { label: "History", value: 1, shouldShow: () => true },
  timeline: { label: "Timeline", value: 2, shouldShow: doc => !doc.IsPositionDocument() },
  phases: {
    label: "Phases",
    value: 3,
    disabled: true,
    shouldShow: doc => doc.IsPositionDocument()
  },
  approvals: {
    label: "Approvals",
    value: 4,
    shouldShow: (_, approvalSettings, canAccessPlanApprovals) =>
      canAccessPlanApprovals && approvalSettings.enablePlanApprovals
  }
};

/**
 * @param {(ChangeDisplayItem | QObjDBObject)} displayItem
 * @returns {void}
 */
export const handleNavigation = displayItem => {
  let override = displayItem;
  if (displayItem.type === "changes") {
    const [change] = displayItem.changes;
    override = change.GetOverride();
  }

  const wsMgr = GetWSMgr();
  const doc = wsMgr.GetDoc();
  const canvasPanel = wsMgr.fCanvasPanel;

  const recordId = override.SmartGetFieldValue("RefID");
  const objType = override.SmartGetFieldValue("objType");

  let navigateId = recordId;
  if (doc.IsPositionDocument()) {
    if (objType === enumObjectTypes.holder) {
      const boxClass = doc.GetBoxClass();
      const displayObject = override.SmartGetFieldValue("displayObject");

      const changeDelta = GetFlattenedChanges(override);
      const isOrphaned = changeDelta["Orphan"] === 1;
      // Fallback: if the assignment status was explicitly changed to Unassigned or Released,
      // also route to the employee list even if the Orphan flag wasn't set.
      const assignmentStatus = changeDelta[kEmployeeAssignmentStatusID];
      const isUnassignedOrReleased =
        assignmentStatus === AssignmentStatus.Unassigned ||
        assignmentStatus === AssignmentStatus.Released;
      if (isOrphaned || isUnassignedOrReleased) {
        let initialQuery = navigateId;
        if (displayObject) {
          const nameValue = displayObject.SmartGetFieldValue(kNameID);
          if (nameValue && !isBlockedField(nameValue)) {
            initialQuery = nameValue;
          }
        }
        wsMgr.BroadcastTopic("OpenEmployeeList", {
          employeeName: initialQuery
        });
        return;
      }

      const empPosFieldDef = boxClass.GetFieldDefByID(kEmployeePositionID);
      const empPosName = empPosFieldDef?.GetFieldName();

      let resolvedToPosition = false;
      if (empPosName && changeDelta[empPosName]) {
        navigateId = changeDelta[empPosName];
        resolvedToPosition = true;
      } else {
        const displayPos = displayObject?.SmartGetFieldValue(kEmployeePositionID);
        if (displayPos) {
          navigateId = displayPos;
          resolvedToPosition = true;
        }
      }

      // Cold cache / missing field def -- fall back to employee list. Without this,
      // the BLS check below would false-positive on the holder ID in the Position class.
      if (!resolvedToPosition) {
        let employeeName = recordId;
        const nameValue = displayObject?.SmartGetFieldValue(kNameID);
        if (nameValue && !isBlockedField(nameValue)) {
          employeeName = nameValue;
        }
        wsMgr.BroadcastTopic("OpenEmployeeList", { employeeName });
        return;
      }
    }
  }

  // BLS scope guard: the loaded document is already BLS-filtered by the server,
  // so if the target record isn't in the document, it's outside the user's scope.
  const branchLevel = wsMgr?.GetCurrentUserGroup()?.GetBranchLevelSecurity();
  const blsProfile = branchLevel?.[doc?.GetChartID?.()];
  const hasActiveBLS = !isNullish(blsProfile) && blsProfile !== true;

  if (hasActiveBLS && doc.IsServerChart()) {
    const aClass = doc.IsPositionDocument()
      ? doc.GetObjDB().GetClassByName("Position")
      : doc.GetObjDB().GetClassByName("Box");
    const recordIdDef = doc.GetSubChartMgr().GetRecordIDField(aClass);

    // persistOnly=true: record absent from the BLS-filtered doc means out of scope.
    if (!aClass.FindObject(recordIdDef, navigateId, true)) {
      wsMgr.CreateReactAlert(
        appstr("Permission denied"),
        appstr("blsNavigationRestricted"),
        () => {},
        undefined,
        appstr("OK")
      );
      return;
    }
  }

  canvasPanel.AddToInitialHighlight(recordId, objType);
  wsMgr.DoNavToBox({ targetID: navigateId }, true);
  ReRenderCanvas();
};

export const Activity = () => {
  const { chartDocument } = useChartDocumentContext();
  const {
    planManager,
    recordEditorContextValue: { retrieveOverrideDBOBj }
  } = usePlanManagerContext();
  const { canAccessPlanApprovals } = usePermissionCheck();

  const [currentTab, setCurrentTab] = useState(() => {
    const stored = localStorage.getItem("plan-activity-section");
    return !isNullish(stored) ? parseInt(stored) : 0;
  });
  const [displayFilters, setDisplayFilters] = useState([]);
  const [searchKey, setSearchKey] = useState("");

  const refsCache = useRef(new Map());
  const searchRef = useRef(null);

  useEffect(() => {
    return () => {
      refsCache.current.clear();
    };
  }, []);

  let planMgr = planManager.state;
  const doc = GetDoc();

  const handleTabChange = currentTabIndex => {
    setCurrentTab(currentTabIndex);
    localStorage.setItem("plan-activity-section", currentTabIndex);
  };

  const onInput = input => {
    setSearchKey(input);
  };

  const handleInput = string => {
    onInput(string);
  };
  const handleResetInput = () => {
    onInput("");
  };

  const handleFilterToggle = value => {
    let filters = [...displayFilters];
    if (filters.includes(value)) filters.splice(filters.indexOf(value), 1);
    else filters.push(value);
    setDisplayFilters(filters);
  };

  const GetAccordionGroup = displayMode => {
    let { overrides: displayOverrides, mismatches } = GetFilteredAndSortedOverrides(
      planMgr,
      displayFilters,
      searchKey
    );
    let accordionGroups = [];
    if (!displayOverrides) return accordionGroups;
    const doc = GetDoc();

    switch (displayMode) {
      case enumDisplayMode.summary.value:
        if (doc.IsPositionDocument()) {
          return GroupOverridesByObjType(displayOverrides, mismatches);
        }

        return GroupOverridesByChangeType(displayOverrides, mismatches);
      case enumDisplayMode.history.value:
        if (doc.IsPositionDocument()) {
          return GroupChangesForHistory(displayOverrides, changeHandlers, itemId => {
            if (!refsCache.current.has(itemId)) {
              refsCache.current.set(itemId, { current: null });
            }
            return refsCache.current.get(itemId);
          });
        }

        return GroupOverridesForHistory(displayOverrides, planMgr);
      case enumDisplayMode.timeline.value:
        let binEffDay = {};
        // Sort by change effective date
        displayOverrides.sort(function (a, b) {
          return planMgr.SortChangeEffectiveDate(a, b);
        });

        // Group by date
        displayOverrides.map(function (override) {
          let changeObj = override.lastActiveChange();
          if (!changeObj) return false;

          // This ensures that changes done on different hours are not grouped together
          let dateString = changeObj.GetChangeEffectiveDate();
          let d = dateString.toDateString();

          binEffDay[d] = binEffDay[d] || [];
          binEffDay[d].push(override);
        });

        // Generate labels
        accordionGroups = Object.keys(binEffDay).map(binID => {
          const readableDate = new Date(binID).GetBinIDFromDate(binEffDay, true);
          let groupLabel = appstrp("Changes effective", " ") + readableDate;
          const childrenOverrides = binEffDay[binID];
          return NormalizeAccordionGroup(groupLabel, childrenOverrides, groupLabel);
        });

        return accordionGroups;
    }
  };

  const handleConflict = displayItem => {
    let override = displayItem;
    if (displayItem.type === "changes") {
      const [change] = displayItem.changes;
      override = change.GetOverride();
    }
    planMgr.handleConflict(override);
  };

  const handleEdit = (displayItem, isMismatch = false) => {
    let override = displayItem;
    if (displayItem.type === "changes") {
      if (displayItem.allChanges.length === 1) {
        const [change] = displayItem.allChanges;
        override = change.GetOverride();
      } else {
        const wsMgr = GetWSMgr();
        const planMgr = wsMgr?.GetPlanMgr();
        if (!planMgr) return;

        const editableOverrides = [];
        const positionIdsToLoad = [];
        const recordIdsToLoad = [];

        for (const change of displayItem.allChanges) {
          const override = change.GetOverride();
          const refId = override?.SmartGetFieldValue("RefID");
          const objType = override?.SmartGetFieldValue("objType");
          const changeType = override?.SmartGetFieldValue("Type");

          if (objType === enumObjectTypes.position) {
            if (changeType !== enumOverrideType.delete) {
              editableOverrides.push(override);
              if (!planMgr.fPositionCloneBaseCache?.[refId]) {
                positionIdsToLoad.push(refId);
              }
            }
          } else {
            editableOverrides.push(override);
            if (!planMgr.fCloneBaseCache?.[refId]) {
              recordIdsToLoad.push(refId);
            }
          }
        }

        if (editableOverrides.length === 0) {
          return;
        }

        const proceedWithEdit = () => {
          const dataObjects = editableOverrides
            .map(override => {
              const refId = override.SmartGetFieldValue("RefID");
              return planMgr.fPositionCloneBaseCache?.[refId];
            })
            .filter(obj => !isNullish(obj));

          if (dataObjects.length === 0) {
            return;
          }

          if (dataObjects.length === 1) {
            planMgr.HandleEditModalOpen(dataObjects[0], enumOverrideType.change);
          } else {
            planMgr.HandleEditMultiModalOpen(dataObjects, enumOverrideType.change);
          }
        };

        if (positionIdsToLoad.length > 0 || recordIdsToLoad.length > 0) {
          planMgr.LoadRecords(
            {
              recordIDs: recordIdsToLoad,
              positionIDs: positionIdsToLoad
            },
            () => {
              proceedWithEdit();
            }
          );
        } else {
          proceedWithEdit();
        }
        return;
      }
    }

    planMgr.handleRecord(override, undefined, undefined, undefined, undefined, isMismatch);
  };

  const handleReopen = displayItem => {
    const wsMgr = GetWSMgr();
    const planMgr = wsMgr?.GetPlanMgr();
    if (!planMgr) return;

    let overridesToReopen = [];
    let positionIdsToLoad = [];

    if (displayItem.type === "changes") {
      for (const change of displayItem.allChanges) {
        const override = change.GetOverride();
        const refId = override?.SmartGetFieldValue("RefID");
        const objType = override?.SmartGetFieldValue("objType");
        const changeType = override?.SmartGetFieldValue("Type");

        if (objType === enumObjectTypes.position && changeType === enumOverrideType.delete) {
          overridesToReopen.push(override);
          if (!planMgr.fPositionCloneBaseCache?.[refId]) {
            positionIdsToLoad.push(refId);
          }
        }
      }
    } else {
      const override = displayItem;
      const refId = override?.SmartGetFieldValue("RefID");
      const objType = override?.SmartGetFieldValue("objType");
      const changeType = override?.SmartGetFieldValue("Type");

      if (objType === enumObjectTypes.position && changeType === enumOverrideType.delete) {
        overridesToReopen = [override];
        if (!planMgr.fPositionCloneBaseCache?.[refId]) {
          positionIdsToLoad = [refId];
        }
      }
    }

    if (overridesToReopen.length === 0) {
      return;
    }

    const proceedWithReopen = () => {
      const dataObjects = overridesToReopen
        .map(override => {
          const refId = override.SmartGetFieldValue("RefID");
          return planMgr.fPositionCloneBaseCache?.[refId];
        })
        .filter(obj => !isNullish(obj));

      if (dataObjects.length > 0) {
        promptToReopenPosition(dataObjects);
      }
    };

    if (positionIdsToLoad.length > 0) {
      planMgr.LoadRecords(
        {
          recordIDs: [],
          positionIDs: positionIdsToLoad
        },
        () => {
          proceedWithReopen();
        }
      );
    } else {
      proceedWithReopen();
    }
  };

  const changeHandlers = { handleConflict, handleEdit, handleNavigation, handleReopen };

  const GetMismatchSublayerOption = mismatch => {
    const employeeName = GetLabelFromMismatch(mismatch, planMgr);

    return {
      name: (
        <ActionListItem
          label={GetChangeAccordionLabel(
            employeeName,
            "Value Mismatch",
            [enumDisplayMode.history, enumDisplayMode.timeline].includes(parseInt(currentTab))
          )}
          labelProps={{ size: "small", modifier: "bold" }}
          actions={GetPlanActivityMismatchActions({ mismatch, changeHandlers })}
        />
      ),
      dataTestId: "plan-accordion-item-Value Mismatch-" + employeeName,
      jsx: <MismatchDisplayRow mismatch={mismatch} />
    };
  };

  const GetChangesSublayerOption = changeDisplayItem => {
    const nameContent = (
      <ActionListItem
        label={
          <Flex gap={"4px"} padding={"0 0 0 4px"}>
            <div className="oc-shrink-0">{GetGroupIconByType(changeDisplayItem.icon)}</div>
            <OverflowText clamp={1} modifier={"bold"} size={"small"}>
              {changeDisplayItem.title}
            </OverflowText>
          </Flex>
        }
        labelProps={{ size: "small", modifier: "bold" }}
        actions={changeDisplayItem.actions}
      />
    );

    const dataTestId = `plan-accordion-item-${changeDisplayItem.value}`;

    const changesContent = (
      <ChangesItemDisplay
        datatestPrefix={dataTestId}
        changes={changeDisplayItem.changes}
        title={changeDisplayItem.title}
        allChanges={changeDisplayItem.allChanges}
      />
    );

    return {
      name: nameContent,
      jsx: changesContent,
      dataTestId: dataTestId,
      divider: true
    };
  };

  const GetStandardSublayerOption = override => {
    const { mostRecentChange, index } = override.lastActiveChange(true);
    // fully reverted overrides will not return any active changes
    if (isNullish(mostRecentChange)) {
      return null;
    }
    const employeeName = mostRecentChange.GetChangeEmployee();

    const changeType = override.SmartGetFieldValue("Type");
    const objType = override.SmartGetFieldValue("objType");
    let iconType = changeType;
    if (doc?.IsPositionDocument()) {
      iconType = objType;
    }

    const recId = override.SmartGetFieldValue("RefID");
    const dataTestId = "plan-accordion-item-" + changeType + "-" + recId;

    let overrideOption = {};
    overrideOption.name = (
      <ActionListItem
        label={GetChangeAccordionLabel(
          employeeName,
          iconType,
          [enumDisplayMode.history, enumDisplayMode.timeline].includes(parseInt(currentTab))
        )}
        labelProps={{ size: "small", modifier: "bold" }}
        actions={BuildPlanActivityOverrideActions({ override, changeHandlers })}
      />
    );
    overrideOption.dataTestId = dataTestId;
    overrideOption.jsx = <ChangesItemDisplay override={override} datatestPrefix={dataTestId} />;
    overrideOption.divider = true;
    return overrideOption;
  };

  const GetSublayerOption = payload => {
    if (!(payload instanceof QObjDBObject)) {
      if (payload.type === "changes") {
        return GetChangesSublayerOption(payload);
      }
      if (payload.type === "mismatch") {
        return GetMismatchSublayerOption(payload.mismatch);
      }

      return GetMismatchSublayerOption(payload);
    }

    return GetStandardSublayerOption(payload);
  };

  const ChangesTab = mode => {
    let accordionData = GetAccordionGroup(mode.value);
    const unfilteredOverrides = GetUnfilteredActiveOverrides(planMgr);

    return (
      <Flex direction="column" className="oc-fill-height">
        <Flex
          className={cx("sidebar-content-container", "oc-flex-1")}
          overflow="hidden"
          gap={"4px"}
        >
          <ActivityHeader
            activeFilters={displayFilters}
            searchKey={searchKey}
            onInput={handleInput}
            overridesShownList={GetFilteredAndSortedOverrides(planMgr, displayFilters, searchKey)}
            unfilteredOverrideList={unfilteredOverrides}
            onResetInput={handleResetInput}
            onFilterChange={handleFilterToggle}
            searchRef={searchRef}
          />
          <Flex flex={1} direction="column" className="ocn-list-scrollbar" overflow="auto">
            {unfilteredOverrides.length === 0 ? (
              <BigInfoIcon
                color="brand"
                name="clipboard-check"
                message={appstr("No changes have been made yet.")}
                style={{ paddingTop: "16px" }}
              />
            ) : (
              (accordionData ?? []).map((group, index) => (
                <ChangeAccordionGroup
                  key={group.value}
                  includeDivider={index > 0}
                  accordionName={GetChangeAccordionLabel(
                    group.title ?? group.name,
                    group.value,
                    parseInt(currentTab) === 0
                  )}
                  initExpand={DoesGroupHaveConflict(group, planMgr)}
                  subLayerOptions={group.children.map(override => GetSublayerOption(override))}
                  otherProps={{ dataTestId: "plan-accordion-group-" + group.value }}
                  removeBeforeContent={true}
                />
              ))
            )}
          </Flex>
        </Flex>
        <ActivityFooter />
      </Flex>
    );
  };

  // Only render the content for the active tab using a switch statement
  const renderTabContent = mode => {
    switch (mode.value) {
      case enumDisplayMode.summary.value:
        return <>{ChangesTab(enumDisplayMode.summary)}</>;
      case enumDisplayMode.history.value:
        return <>{ChangesTab(enumDisplayMode.history)}</>;
      case enumDisplayMode.timeline.value:
        return <>{ChangesTab(enumDisplayMode.timeline)}</>;
      case enumDisplayMode.phases.value:
        return <>{ChangesTab(enumDisplayMode.phases)}</>;
      case enumDisplayMode.approvals.value:
        return <ApprovalsTab />;
      default:
        return null;
    }
  };

  const GetTabs = () => {
    let tabs = [];
    for (let key in enumDisplayMode) {
      let mode = enumDisplayMode[key];
      const shouldShow = mode.shouldShow(
        doc,
        planMgr.GetApprovalSettings(),
        canAccessPlanApprovals
      );
      if (!shouldShow) {
        continue;
      }

      let tabInfo = {};
      tabInfo.disabled = mode.disabled ?? false;
      tabInfo.header = appstr(mode.label);
      tabInfo.content = <Fragment key={tabInfo.header}>{renderTabContent(mode)}</Fragment>;

      tabs.push(tabInfo);
    }
    return tabs;
  };

  return (
    <React.Fragment>
      <Tabs
        tabSize={"medium"}
        tabIndex={parseInt(currentTab)}
        customClass={"ocn-sidebar-tabs"}
        customContentClass={"ocn-chart-explorer-tabs-content"}
        customHeaderClass={"oc-tab-header-no-divider"}
        tabs={GetTabs()}
        onTabChange={handleTabChange}
      />
    </React.Fragment>
  );
};
